import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { activateAdmin, verifyActivationToken } from "../../admins/api/adminsApi";
import type { VerifyTokenResponse } from "../../admins/types";

const ActivatePage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [tokenInfo, setTokenInfo] = useState<VerifyTokenResponse | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setError("Token inválido");
        setIsLoading(false);
        return;
      }

      try {
        const result = await verifyActivationToken(token);
        setTokenInfo(result);
        if (!result.valid) {
          setError("El enlace de invitación es inválido o ha expirado");
        }
      } catch {
        setError("Error al verificar el enlace");
      } finally {
        setIsLoading(false);
      }
    };

    verify();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres");
      return;
    }

    if (password !== confirmPassword) {
      setError("Las contraseñas no coinciden");
      return;
    }

    if (!token) return;

    setIsSubmitting(true);
    try {
      await activateAdmin({ token, password });
      setIsSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Error al activar cuenta";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-slate-400">Verificando enlace...</div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-full max-w-md rounded-lg border border-slate-700 bg-surface p-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-500/20">
            <svg
              className="h-8 w-8 text-green-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="mb-2 text-xl font-bold text-slate-100">¡Cuenta activada!</h1>
          <p className="text-slate-400">Redirigiendo al login...</p>
        </div>
      </div>
    );
  }

  if (!tokenInfo?.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-full max-w-md rounded-lg border border-slate-700 bg-surface p-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-danger/20">
            <svg
              className="h-8 w-8 text-danger"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h1 className="mb-2 text-xl font-bold text-slate-100">Enlace inválido</h1>
          <p className="text-slate-400">
            {error || "El enlace de invitación es inválido o ha expirado."}
          </p>
          <button
            onClick={() => navigate("/login")}
            className="mt-6 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primaryAccent"
          >
            Ir al login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md rounded-lg border border-slate-700 bg-surface p-8">
        <div className="mb-6 text-center">
          <h1 className="mb-2 text-2xl font-bold text-slate-100">Activar cuenta</h1>
          <p className="text-slate-400">
            Hola {tokenInfo.firstName}, configura tu contraseña para acceder al backoffice.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="rounded-md bg-slate-800/50 p-3">
            <p className="text-sm text-slate-300">
              <span className="text-slate-500">Email:</span> {tokenInfo.email}
            </p>
          </div>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase text-slate-400">Contraseña</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-md border border-slate-700 bg-background px-3 py-2 text-sm text-slate-100 focus:border-primary focus:outline-none"
              placeholder="Mínimo 8 caracteres"
              required
              minLength={8}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase text-slate-400">
              Confirmar contraseña
            </span>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="rounded-md border border-slate-700 bg-background px-3 py-2 text-sm text-slate-100 focus:border-primary focus:outline-none"
              placeholder="Repite tu contraseña"
              required
            />
          </label>

          {error && <p className="rounded-md bg-danger/10 p-3 text-sm text-danger">{error}</p>}

          <button
            type="submit"
            className="mt-2 rounded-md bg-primary px-4 py-3 text-sm font-semibold text-white transition hover:bg-primaryAccent disabled:opacity-50"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Activando..." : "Activar mi cuenta"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ActivatePage;
